import {
  CallId,
  ContactFirstName,
  ContactLastName,
  ContactIntegratedList,
  IntegratedContactsResponseList,
} from '~/interfaces/integrations';

import log from './log';

export const integrationContactState = {
  /**
   * Means contact is already saved on CRM & Database
   */
  CREATED: 'CREATED',
  /**
   * Means the contact is draft locally to be saved when use click to save contact
   */
  DRAFT: 'DRAFT',
};

export const IntegrationIds = {
  AUTO_TASK: 56,
  ZENDESK_ID: 18,
  ONE_PAGE_CRM: 58,
  SALESFORCE: 22,
  BULLHORN: 63,
  HUBSPOT_ID: 34,
  ZOHO_ID: 42,
  GO_HIGH_LEVEL_ID: 65,
  CUSTOM_URLS_ID: 59, // As in calllogs
  ACTIVE_CAMPAIGN_ID: 46,
  ODOO_ID: 57, // Can have sub accounts 57.200
};

const { SALESFORCE, BULLHORN, GO_HIGH_LEVEL_ID, ZOHO_ID } = IntegrationIds;

export function composeFullName(
  firstName: ContactFirstName,
  lastName: ContactLastName
) {
  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return `${firstName}`;
  if (lastName) return `${lastName}`;
  return null;
}

/**
 * BULLHORN SPECIFIC FEATURE
 * Extract information from contact externalId string
 */
export function extractInfoFromExternalId(externalId: string) {
  if (typeof externalId !== 'string') return undefined;

  if (externalId.includes('/')) {
    return {
      bullhornType: externalId.split('/')[0],
      bullhornId: externalId.split('/')[1],
    };
  }

  return undefined;
}

/**
 * Define external contact URL to access integrations page.
 * onnectedIntegrations - Available connected integrations
 * integrationId - Represent the integraionId (exemple: 30 pipedrive / 34 hubspot)
 * externalId - Represent external contactId
 */
export function composeExternalUrl({
  connectedIntegrations,
  integrationId,
  externalId,
  url,
}: {
  connectedIntegrations: any;
  integrationId: string | number | undefined;
  externalId: string;
  url: string | undefined;
}): string | null {
  if (!connectedIntegrations || !integrationId || !externalId) return null;

  if (connectedIntegrations?.length <= 0) return null;

  if (typeof url === 'string' && url.length > 1) return url;

  // FIXME: Refactor connectedCRMList data fetching, define Number type there
  const findIntegration = connectedIntegrations.find(
    // @ts-ignore
    e => Number(e.id) === integrationId
  );

  const contactUrl = findIntegration
    ? findIntegration?.actionUrl?.contact_url
    : null;

  if (contactUrl && externalId) {
    if (integrationId === SALESFORCE) {
      return contactUrl.replace('[external_id]', externalId);
    }

    if (String(integrationId).startsWith(GO_HIGH_LEVEL_ID.toString())) {
      // ExternalId is compoused so we remove the contact type from the Id
      return contactUrl + externalId.split('/')[1];
    }

    if (integrationId === BULLHORN) {
      const bullhornUrlInfo = extractInfoFromExternalId(externalId);
      if (bullhornUrlInfo) {
        const { bullhornType, bullhornId } = bullhornUrlInfo;

        return contactUrl
          .replace('[type]', bullhornType)
          .replace('[external_id]', bullhornId);
      }
    }

    if (integrationId === ZOHO_ID) {
      const contactType = externalId.split('/')[0];
      const contactId = externalId.split('/')[1];
      return contactUrl
        .replace('[ContactType]', contactType)
        .replace('[ContactId]', contactId);
    }

    return contactUrl + externalId;
  }

  return contactUrl;
}

export function aggregateIntegrationDetails(
  integrationId: string | number | undefined,
  connectedIntegrations: any
): { logo: string; name: string } {
  const connectedInt = connectedIntegrations
    // @ts-ignore
    ?.find(e => Number(e.id) === integrationId);

  if (connectedInt?.logo && connectedInt?.name) {
    return connectedInt;
  }

  return { logo: '', name: '' };
}

/**
 * This function is used to parse the integration contacts
 * and return them in a standard format.
 * @param contacts - The data gathered from the contacts API.
 * @param callId - The current call id.
 * @param connectedIntegrations - Available connected integrations
 */
const standardizeContacts = (
  contacts: IntegratedContactsResponseList,
  callId: CallId,
  connectedIntegrations: any,
  phoneNumber: string,
  integrationAccountId: number | undefined
) => {
  try {
    const groupedStandardizeContacts: ContactIntegratedList = [];

    contacts.forEach(con => {
      // JS Conversion: Snake-Case to CamelCase
      const contactPropsInCamelCase = {
        firstName: con.firstname,
        lastName: con.lastname,
        companyName: con.company_name,
        externalId: con.external_id,
        externalPortalId: con.external_portal_id,
        ownerName: con.owner_name,
        /**
         * GO HIGH LEVEL HAS SUB-ACCOUNTS FEATURE FROM NWA-2438
         * THE API ALTHOUGH RETURNS THE MAIN INTEGRATION ID (65), NOT THE SUB-ACCOUNT ID (E.G. 65.200)
         */
        integrationId: String(con?.integration_id).startsWith(
          IntegrationIds.GO_HIGH_LEVEL_ID.toString()
        )
          ? Number(`${con?.integration_id}.${integrationAccountId}`)
          : con?.integration_id || Number(con?.integration),
      };

      const {
        firstName,
        lastName,
        integrationId,
        externalId,
      } = contactPropsInCamelCase;

      const contactNewLocalPropsAndCapabilities = {
        ...contactPropsInCamelCase,
        callId,
        contactState: integrationContactState.CREATED,
        phoneNumber,
        fullNameFormatted: composeFullName(firstName, lastName),
        // displayName: displayNameParser(data),
        externalUrl: composeExternalUrl({
          url: con?.url,
          connectedIntegrations,
          integrationId,
          externalId,
        }),
        integrationInfo: aggregateIntegrationDetails(
          integrationId,
          connectedIntegrations
        ),
        // Indicate wether the contact will be synced or not on CRM
        callSyncWithCRM: true,
        // Note: It's important to re-dispatch the entire objects to reducer anyways
        setCallSyncWithCRM(callSyncNewStatus: boolean) {
          this.callSyncWithCRM = callSyncNewStatus;
        },
      };

      // @ts-ignore
      groupedStandardizeContacts.push(contactNewLocalPropsAndCapabilities);
    });

    return groupedStandardizeContacts;
  } catch (err) {
    log.error('Failure to format the integration contact: Reason: ', err);
  }

  return [];
};

export { standardizeContacts };
